//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion

//#region Helper Imports

import { DataProvider, Dictionary, ReferenceTypeComponent, ConvertHelper, DataTypeGroupComponent, ConfigHelper, LogHelper, TypeHelper } from "../helpers"
import { KeyValuePair, KeyType } from '../helpers/dictionary'

//#endregion

//#region Model Imports

import { ColumnModel } from '../modules/api/api.models'

import { OpFile, OpDataTypeInGroup, OpViewColumn } from "../models/core"
import { Enums } from "../models/common"
import { ColumnInfo, OpSchema, OpHistory } from "../models/custom"
import { OpImportData, OpImportItemSummary, OpImportProgressStep, OpImportSummary } from '../models/util'
import { IReferenceType } from '../models/util/interfaces'
import { OpExportPluginParam } from '../models/util'

//#endregion

import { DataProviderListResult } from './data-provider.interfaces'
import { IndexedParamCacheStrategy } from './cache-strategies'
import { DataProviderRequest } from './utils'

type GetRequestColumnInfoOptions = {
    groupReferenceType?: Enums.ReferenceType,
    groupReferenceValue?: number,
    viewReferenceType?: Enums.ReferenceType
};

export class DataProviderSystem {

    //#region Consts

    private readonly LOG_PREFIX = '[SIMAX.DataProvider.System]'

    //#endregion

    //#region Ctor

    public constructor(private dataProvider: DataProvider) { }

    //#endregion

    //#region Dictionary

    public getDictionaries = (dictionariesTypes: Enums.ReferenceType[], queryInterface: boolean = false): JQueryPromise<Dictionary<Dictionary<IReferenceType>>> => {
        const deferred = $.Deferred()

        if (_.size(dictionariesTypes) > 0) {
            const dictionariesTypesQuerryInterface: Enums.ReferenceType[] = this.getDictionaryTypesToLoad(dictionariesTypes, queryInterface)

            if (_.size(dictionariesTypesQuerryInterface) > 0) {
                this.requestDictionaries(dictionariesTypesQuerryInterface)
                    .then((loadedDictionaries: any[]) => {
                        if (_.size(loadedDictionaries)) {
                            this.saveDictionariesToLocalStorage(loadedDictionaries, dictionariesTypesQuerryInterface)
                                .then(() => {
                                    deferred.resolve()
                                })
                        }
                        else {
                            deferred.resolve()
                        }
                    })
            }
            else {
                deferred.resolve()
            }
        }

        return deferred.then(() => {
            const result: Dictionary<Dictionary<IReferenceType>> = new Dictionary<Dictionary<IReferenceType>>(null)

            dictionariesTypes.forEach(d => {
                result.add(d, this.dataProvider.LocalStorage.tryGetDictionary(d))
            })

            return result
        })
    }

    private getDictionaryTypesToLoad(dictionaryTypes: Enums.ReferenceType[], queryInterface: boolean) {
        let dictionaryTypesToLoad: Enums.ReferenceType[] = []

        if (queryInterface) {
            dictionaryTypesToLoad = TypeHelper.clone(dictionaryTypes)
        }
        else {
            dictionaryTypesToLoad = this.getNonLoadedDictionaryTypes(dictionaryTypes)
        }

        if (_.size(dictionaryTypesToLoad) > 0) {
            const dependantDictionaryTypes: Enums.ReferenceType[] = this.getDependandDictionaryTypes(dictionaryTypesToLoad)

            dictionaryTypesToLoad = [...dictionaryTypesToLoad, ...dependantDictionaryTypes]
            dictionaryTypesToLoad = _.uniq(dictionaryTypesToLoad)
            dictionaryTypesToLoad = ReferenceTypeComponent.orderReferenceObjectTypeByHierarchy(dictionaryTypesToLoad)
        }

        return dictionaryTypesToLoad
    }

    private getNonLoadedDictionaryTypes(dictionaryTypes: Enums.ReferenceType[]): Enums.ReferenceType[] {
        const dictionaryTypesToLoad: Enums.ReferenceType[] = []

        dictionaryTypes.forEach((referenceType: Enums.ReferenceType) => {
            if (!this.dataProvider.LocalStorage.dictionaries.containsKey(referenceType)) {
                dictionaryTypesToLoad.push(referenceType)
            }
        })

        return dictionaryTypesToLoad
    }

    private getDependandDictionaryTypes(dictionaryTypes: Enums.ReferenceType[]): Enums.ReferenceType[] {
        const additionalDictionariesTypesQuerryInterface: Enums.ReferenceType[] = []
        const dependantDictionaryTypes: Enums.ReferenceType[] = []

        for (let _i = 0; _i < dictionaryTypes.length; _i++) {
            ReferenceTypeComponent.getDependandReferenceObjectTypes(dictionaryTypes[_i]).forEach(d => {
                additionalDictionariesTypesQuerryInterface.push(d)
            })
        }

        additionalDictionariesTypesQuerryInterface.forEach(r => {
            if (!this.dataProvider.LocalStorage.dictionaries.containsKey(r)) {
                dependantDictionaryTypes.push(r)
            }
        })

        return dependantDictionaryTypes
    }

    private requestDictionaries(dictionaryTypes: number[]): JQueryPromise<any> {
        const requestParams = { DictionariesTypes: dictionaryTypes }
        const cacheStrategy = new IndexedParamCacheStrategy<typeof requestParams>({
            getIndexedParams: (params) => {
                return params.DictionariesTypes
            },
            setIndexedParams: (params, cachedParams) => {
                params.DictionariesTypes = cachedParams
            },
            ignoredIndexes: [Enums.ReferenceType.IdDeviceUdiDescription]
        })

        return this.dataProvider.sendCachedRequestWithSession('GetDictionaries', requestParams, 'GetDictionariesResult', cacheStrategy)
    }

    private saveDictionariesToLocalStorage(loadedDictionaries: any[], dictionaryTypes: Enums.ReferenceType[]) {
        const result = $.Deferred()

        this.dataProvider.LocalStorage
            .getDictionaries()
            .then((localStorageDictionaries: Dictionary<any>) => {
                let promise = $.when<any>()

                if (!localStorageDictionaries) {
                    localStorageDictionaries = new Dictionary<any>(null)
                }

                for (let i = 0; i < loadedDictionaries.length; i++) {
                    const referenceType: Enums.ReferenceType = dictionaryTypes[i]
                    const dictionary: any[] = loadedDictionaries[i]
                    const collectionToSet: Dictionary<IReferenceType> = new Dictionary<IReferenceType>(null)
                    const collectionToLocalStorage: Dictionary<IReferenceType> = new Dictionary<IReferenceType>(null)

                    if (_.size(dictionary) > 0) {
                        dictionary.forEach(d => {
                            const referenceObject: IReferenceType = ReferenceTypeComponent.createReferenceObject(referenceType, d)

                            if (referenceObject != null) {
                                collectionToSet.add(referenceObject.getReferenceKey(), referenceObject)
                                collectionToLocalStorage.add(referenceObject.getReferenceKey(), ReferenceTypeComponent.createReferenceObject(referenceType, d))
                            }
                            else {
                                LogHelper.warn(this.LOG_PREFIX, `Unable to save idReferenceType ${referenceType} to local storage.`)
                            }
                        })
                    }

                    localStorageDictionaries.addOrUpdate(referenceType, collectionToLocalStorage)

                    if (collectionToSet && collectionToSet.length > 0) {
                        promise = promise.then(() => {
                            return ReferenceTypeComponent
                                .loadReferenceObjectNavigationProperties(this.dataProvider, referenceType, collectionToSet.values())
                                .then(() => {
                                    this.dataProvider.LocalStorage.setDictionary(referenceType, collectionToSet)
                                })
                        })
                    }
                }

                this.dataProvider.LocalStorage.saveDictionaries(localStorageDictionaries)

                promise.then(() => {
                    result.resolve()
                })
            })

        return result
    }

    public getDictionariesLocal = (referenceTypes: Enums.ReferenceType[]): Dictionary<Dictionary<IReferenceType>> => {
        const result: Dictionary<Dictionary<IReferenceType>> = new Dictionary<Dictionary<IReferenceType>>(null)

        if (referenceTypes && referenceTypes.length > 0) {
            const dicts: Dictionary<Dictionary<IReferenceType>> = this.dataProvider.LocalStorage.getDictionariesLocal()

            if (dicts != null) {
                referenceTypes.forEach((referenceType: Enums.ReferenceType) => {
                    if (!dicts.containsKey(referenceType)) {
                        console.warn(`Unable to load a dictionary for referenceType: ${referenceType} locally.`)
                        result.addOrUpdate(referenceType, null)
                    }
                    else {
                        result.addOrUpdate(referenceType, dicts.tryGetValue(referenceType))
                    }
                })

                return result
            }
        }

        console.warn(`Unable to load dictionaries for referenceTypes: [${referenceTypes}] locally.`)
        return result
    }

    public getDictVersion = (querryInterface: boolean = false): JQueryPromise<number> => {
        var deferred: JQueryDeferred<number> = $.Deferred<number>()

        if (!this.dataProvider.LocalStorage.getLoggedOperatorSetting(Enums.SimaxOperatorSetting.DictionaryVersion) || querryInterface) {
            this.dataProvider
                .sendRequestWithSession('GetDictVersion', {}, 'GetDictVersionResult')
                .then(result => {
                    this.dataProvider
                        .setDataProviderContext(null, null, result)
                        .then(() => {
                            deferred.resolve(result)

                        })
                })
        }
        else {
            deferred.resolve(this.dataProvider.LocalStorage.getLoggedOperatorSetting(Enums.SimaxOperatorSetting.DictionaryVersion))
        }

        return deferred.promise()
    }

    //#endregion

    //#region Reference Objects

    /**
     * Zamienić na getReferenceObjectList po odblokowaniu plików
     */
    public getReferenceObjectList<TReferenceType extends IReferenceType>(
        referenceType: Enums.ReferenceType,
        referenceValues: any[],
        columns: ColumnInfo[],
        pageNbr: number,
        pageSize: number,
        customWhereClause: string,
        contextIdDataType: number,
    ): JQueryPromise<DataProviderListResult<TReferenceType>> {
        return this.dataProvider.sendFilterRequestWithSession(
            columns,
            pageNbr,
            pageSize,
            customWhereClause,
            referenceType,
            'GetReferenceObjectList',
            'GetReferenceObjectListResult',
            {
                ReferenceType: referenceType,
                ReferenceValues: referenceValues,
                ContextIdDataType: contextIdDataType
            }
        )
    }

    //#endregion

    //#region Schema

    public getSchema = (referenceType: Enums.ReferenceType, id: number[]): DataProviderRequest<OpSchema[]> => {
        const promise: JQueryPromise<OpSchema[]> = this.dataProvider
            .sendRequestWithSession('OpxGetSchema', { IdObject: id, ReferenceType: referenceType }, 'OpxGetSchemaResult')
            .then((response: any[]) => DataProvider.convertArrayResult<OpSchema>(response, OpSchema))

        return DataProviderRequest.fromJQuery(promise)
    }

    public saveSchema = (referenceType: Enums.ReferenceType, schema: OpSchema): JQueryPromise<OpSchema> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveSchema', { toSave: schema.toJSON(), ReferenceType: referenceType }, 'OpxSaveSchemaResult')
            .then((response: any[]) => { return new OpSchema(response) })
    }

    //#endregion

    //#region Flow

    public getFlow = <TStatusFlow>(idDataType: Enums.DataType): JQueryPromise<TStatusFlow[]> => {
        let operatorSetting: Enums.SimaxOperatorSetting = null

        switch (idDataType) {
            case Enums.DataType.HELPER_ID_ISSUE_STATUS:
                operatorSetting = Enums.SimaxOperatorSetting.IssueStatusFlow
                break

            case Enums.DataType.HELPER_ID_TASK_STATUS:
                operatorSetting = Enums.SimaxOperatorSetting.TaskStatusFlow
                break

            case Enums.DataType.HELPER_ID_PACKAGE_STATUS:
                operatorSetting = Enums.SimaxOperatorSetting.PackageStatusFlow
                break

            case Enums.DataType.HELPER_ID_ORDER_STATUS:
                operatorSetting = Enums.SimaxOperatorSetting.OrderStatusFlow
                break
        }

        if (operatorSetting == null) {
            LogHelper.warn(this.LOG_PREFIX, `Unable to load flow for dataType: ${ConvertHelper.enumToString(Enums.DataType, idDataType)}. Returning an empty list.`)

            return $.Deferred<TStatusFlow[]>().resolve([]).promise()
        }

        return this.dataProvider.Operator
            .getOperatorSettings(this.dataProvider.Operator.getLastIdOperator(), [operatorSetting])
            .then((settings: [TStatusFlow[]]) => {
                if (settings && settings.length > 0) {
                    return settings[0]
                }

                return $.Deferred<TStatusFlow[]>().reject("No settings or settings list empty.")
            })
            .fail((error: any) => {
                LogHelper.warn(this.LOG_PREFIX, `Unable to load flow for dataType: ${ConvertHelper.enumToString(Enums.DataType, idDataType)}. Returning an empty list.`, ConvertHelper.getRequestErrorMessage(error))

                return []
            })
    }

    public getFlowByStatus = <TStatusFlow extends { changeStatus: TStatus[] }, TStatus>(idDataType: Enums.DataType, filter: (status: TStatusFlow) => boolean): JQueryPromise<TStatus[]> => {
        return this.dataProvider.System
            .getFlow<TStatusFlow>(idDataType)
            .then((statusFlow: TStatusFlow[]) => {
                const currentFlow: TStatusFlow = _.find(statusFlow, filter)

                if (currentFlow != null) {
                    return currentFlow.changeStatus
                }

                LogHelper.warn(this.LOG_PREFIX, `Unable to next load flow for status. Returning an empty list.`)

                return []
            })
    }

    //#endregion

    //#region History

    public getHistory = (idObject: number, referenceType: Enums.ReferenceType): JQueryPromise<OpHistory[]> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetHistory', { IdObject: idObject, ReferenceType: referenceType }, 'OpxGetHistoryResult')
            .then(data => {
                var result: OpHistory[] = DataProvider.convertArrayResult<OpHistory>(data, OpHistory)

                return result
            })
    }

    public getHistories = (ids: number[], referenceType: Enums.ReferenceType): JQueryPromise<Dictionary<OpHistory[]>> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetHistories', { idObjects: ids, referenceType: referenceType }, 'OpxGetHistoriesResult')
            .then((response: any) => {
                const dict: Dictionary<OpHistory[]> = new Dictionary<OpHistory[]>(null, KeyType.Number)

                if (response != null) {
                    response.forEach((data: any) => {
                        dict.add(data.Key, _.size(data.Value) > 0 ? data.Value.map((history: any) => new OpHistory(history)) : null)
                    })
                }

                return dict
            })
    }

    //#endregion

    //#region Allowed List

    public getAllowedList = (referenceTypes: Enums.ReferenceType[]): JQueryPromise<Dictionary<Dictionary<IReferenceType>>> => {
        return this.dataProvider
            .sendRequestWithSession('GetAllowedList', { ReferenceTypes: referenceTypes }, 'GetAllowedListResult')
            .then(data => {
                var result: Dictionary<Dictionary<IReferenceType>> = new Dictionary<Dictionary<IReferenceType>>(null)
                if (data && data.length > 0) {
                    referenceTypes.forEach(r => {
                        var refTypeList: KeyValuePair<IReferenceType>[] = []
                        var refTypeArray: any[] = data[referenceTypes.indexOf(r)]
                        var refValue: IReferenceType
                        if (refTypeArray && refTypeArray.length > 0) {
                            refTypeArray.forEach(obj => {
                                refValue = ReferenceTypeComponent.createReferenceObject(r, obj)
                                refTypeList.push(new KeyValuePair<IReferenceType>(refValue.getReferenceKey(), refValue))
                            })
                        }
                        result.addOrUpdate(r, new Dictionary<IReferenceType>(refTypeList))
                    })
                }
                return result
            })
    }

    //#endregion

    //#region App

    public checkAppVersion = (appVersion: string): boolean => {
        const lastAppVersion: string = this.dataProvider.LocalStorage.getFromLocalStorage(Enums.SimaxLocalStorageKey.AppVersion)

        if (appVersion != lastAppVersion) {
            LogHelper.warn(this.LOG_PREFIX, 'Version mismatch. Clearing local storage.')

            try {
                this.dataProvider.LocalStorage.clearLocalStorage()
                this.dataProvider.LocalStorage.saveToLocalStorage(Enums.SimaxLocalStorageKey.AppVersion, appVersion, 0)
            }
            catch (e) {
                LogHelper.warn(this.LOG_PREFIX, 'Version mismatch. Unable To clear localStorage.', e)

                this.dataProvider.LocalStorage.clearLocalStorage()
            }

            return false
        }

        return true
    }

    public hardReset = (): void => {
        this.dataProvider.LocalStorage.clearLocalStorage()
        location.reload()
    }

    public getCurrentLanguage = () => {
        let currentLocale = this.dataProvider.LocalStorage.getFromLocalStorage(Enums.SimaxLocalStorageKey.CultureName, null)
        if (!currentLocale) {
            let defaultLocale = ConfigHelper.getDefaultValue<{ locale: string }>(Enums.SimaxConfigSection.Languages).locale
            this.dataProvider.LocalStorage.saveToLocalStorage(Enums.SimaxLocalStorageKey.CultureName, defaultLocale)
            return defaultLocale
        }
        return currentLocale
    }

    public getCurrentLanguageId = () => {
        const currentLocale = this.dataProvider.System.getCurrentLanguage()
        if (currentLocale != null) {
            const allLanguages: { locale: string, id: number }[] = ConfigHelper.getSectionValue(Enums.SimaxConfigSection.Languages)
            const currentLanguage = allLanguages.filter(l => l.locale == currentLocale)[0]
            if (currentLanguage != null)
                return currentLanguage.id
        }
        console.warn('Can\'t get current language id')
    }

    public setCurrentLanguage = (locale: string) => {
        if (locale == null) {
            locale = ConfigHelper.getDefaultValue(Enums.SimaxConfigSection.Languages).locale
        }
        this.dataProvider.LocalStorage.saveToLocalStorage(Enums.SimaxLocalStorageKey.CultureName, locale, 0)
    }

    //#endregion

    //#region Imports

    public importIsImportCompleted = (referenceType: Enums.ReferenceType, dllName: string): JQueryPromise<boolean> => {
        return this.dataProvider.sendRequestWithSession('OpxImportIsCompleted', { ReferenceType: referenceType, DllName: dllName }, 'OpxImportIsCompletedResult')
    }

    public importGetData = (referenceType: Enums.ReferenceType, importGuid: string): JQueryPromise<OpImportData> => {
        return this.dataProvider
            .sendRequestWithSession('OpxImportGetData', { ReferenceType: referenceType, ImportGuid: importGuid }, 'OpxImportGetDataResult')
            .then((result: any) => {
                if (result != null) {
                    return new OpImportData(result)
                }

                return null
            })
    }

    public importSummary = (referenceType: Enums.ReferenceType, importGuid: string): JQueryPromise<OpImportItemSummary[]> => {
        return this.dataProvider.sendRequestWithSession('OpxImportSummary', { ReferenceType: referenceType, ImportGuid: importGuid }, 'OpxImportSummaryResult').then(data => {
            if (!data)
                return null
            return data.map(item => new OpImportItemSummary(item))
        })
    }

    public importInitialize = (referenceType: Enums.ReferenceType, importData: OpImportData, dllName: string): JQueryPromise<string> => {
        return this.dataProvider.sendRequestWithSession('OpxImportInitialize', { ReferenceType: referenceType, ImportData: importData.toJSON(), DllName: dllName }, 'OpxImportInitializeResult')
    }

    public importProgress = (referenceType: Enums.ReferenceType, importGuid: string): JQueryPromise<OpImportProgressStep> => {
        return this.dataProvider.sendRequestWithSession('OpxImportProgress', { ReferenceType: referenceType, ImportGuid: importGuid }, 'OpxImportProgressResult').then(data => {
            if (!data)
                return null
            return new OpImportProgressStep(data)
        })
    }

    public importValidateData = (referenceType: Enums.ReferenceType, importGuid: string): JQueryPromise<OpImportItemSummary[]> => {
        return this.dataProvider.sendRequestWithSession('OpxImportValidateData', { ReferenceType: referenceType, ImportGuid: importGuid }, 'OpxImportValidateDataResult').then(data => {
            if (!data)
                return null
            return data.map(item => new OpImportItemSummary(item))
        })
    }

    public importStartImport = (referenceType: Enums.ReferenceType, importGuid: string): JQueryPromise<Enums.ImportStatus> => {
        return this.dataProvider.sendRequestWithSession('OpxImportStartImport', { ReferenceType: referenceType, ImportGuid: importGuid }, 'OpxImportStartImportResult')
    }

    public importStopImport = (referenceType: Enums.ReferenceType, importGuid: string): JQueryPromise<void> => {
        return this.dataProvider.sendRequestWithSession('OpxImportStopImport', { ReferenceType: referenceType, ImportGuid: importGuid }, null)
    }

    public importResume = (referenceType: Enums.ReferenceType, dllName: string): JQueryPromise<OpImportSummary> => {
        return this.dataProvider
            .sendRequestWithSession('OpxImportResume', { ReferenceType: referenceType, DllName: dllName }, 'OpxImportResumeResult')
            .then((result: any) => {
                if (result != null) {
                    return new OpImportSummary(result)
                }

                return null
            })
    }

    public importGetTemplate = (referenceType: Enums.ReferenceType, importData: OpImportData, dllName: string): JQueryPromise<OpFile> => {
        return this.dataProvider
            .sendRequestWithSession('OpxImportGetTemplate', { ReferenceType: referenceType, ImportData: (importData == null ? null : importData.toJSON()), DllName: dllName }, 'OpxImportGetTemplateResult')
            .then((data: any) => new OpFile(data))
    }

    //#endregion

    //#region Export

    public export = (referenceType: Enums.ReferenceType, columns: ColumnInfo[], customWhereClause: string, dllName: string, params: OpExportPluginParam[]) => {
        const requestArgs: any = {
            ReferenceType: referenceType,
            Columns: columns,
            CustomWhereClause: customWhereClause,
            DllName: dllName,
            exportParams: params != null ? params.map((param: OpExportPluginParam) => param.toJSON()) : null
        }

        return this.dataProvider
            .sendRequestWithSession('OpxExport', requestArgs, 'OpxExportResult')
            .then((data: any) => new OpFile(data))
    }

    //#endregion

    //#region Context List

    public getContextList = (sourceType: Enums.ReferenceType, destinationType: Enums.ReferenceType, sourceReferenceValues: any[]): JQueryPromise<IReferenceType[]> => {
        return this.dataProvider
            .sendRequestWithSession('GetContextList', { SourceType: sourceType, DestinationType: destinationType, sourceReferenceValues: sourceReferenceValues }, 'GetContextListResult')
            .then((result: any[]) => {
                if (_.size(result) > 0) {
                    return result.map((item: any) => ReferenceTypeComponent.createReferenceObject(destinationType, item))
                }
                return []
            })
    }

    //#endregion

    /**
     * @deprecated Use GridService.getColumnModelByCategory
     */
    public getRequestColumnInfo = (
        dataTypeGroupCategory: Enums.SimaxDataTypeGroupCategory,
        moduleName: Enums.SimaxModuleName,
        { groupReferenceType, groupReferenceValue, viewReferenceType }: GetRequestColumnInfoOptions = {}
    ): JQueryPromise<ColumnModel[]> => {
        const deferred: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()
        const referenceType: Enums.ReferenceType = viewReferenceType != null ? viewReferenceType : ReferenceTypeComponent.getReferenceTypeByModule(moduleName)
        const result: ColumnModel[] = []

        if (!referenceType || referenceType == Enums.ReferenceType.None as Enums.ReferenceType) {
            deferred.resolve(result)
        } else {
            DataTypeGroupComponent.getDataTypeInGroupByModule(this.dataProvider, dataTypeGroupCategory, moduleName, groupReferenceType, groupReferenceValue).then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                if (!dataTypeInGroup || dataTypeInGroup.length == 0) {
                    if (window['config'].DEBUG) {
                        console.info('No data types in group founded for request column info. Category: ' + Enums.SimaxDataTypeGroupCategory[dataTypeGroupCategory] + ', Module name: ' + moduleName)
                    }

                    deferred.resolve(result)
                } else {
                    this.dataProvider.View.getActiveView(referenceType).then(activeView => {
                        if (!activeView) {
                            if (window['config'].DEBUG) {
                                console.info('No view found: ' + referenceType)
                            }

                            deferred.resolve(result)
                        } else {
                            const result: ColumnModel[] = []

                            activeView.columns
                                .forEach((viewColumn: OpViewColumn) => {
                                    dataTypeInGroup.forEach((dtgItem: OpDataTypeInGroup) => {
                                        if (viewColumn.idDataType == dtgItem.idDataType && dtgItem.dataType != null) {
                                            const column: ColumnModel = new ColumnModel()

                                            column.id = dtgItem.idDataType
                                            column.caption = dtgItem.toString()
                                            column.name = viewColumn.name.toLowerCase()
                                            column.type = dtgItem.dataType.idDataTypeClass
                                            column.referenceTypeId = dtgItem.dataType.idReferenceType
                                            column.isEditable = dtgItem.dataType.isEditable
                                            column.format = <any>dtgItem.dataType.dataTypeFormat
                                            column.unitId = dtgItem.dataType.idUnit
                                            column.unitName = dtgItem.dataType.unit.toString()
                                            column.dataType = dtgItem.dataType
                                            column['orderNbr'] = dtgItem.orderNBR
                                            column.skipTranslation = viewColumn.shouldSkipTranslation
                                            column.hidden = viewColumn.isHidden
                                            column.alwaysHidden = viewColumn.isHidden
                                            column.showInColumnChooser = !viewColumn.isHidden

                                            result.push(column)
                                        }
                                    })
                                })

                            result.sort((columnModel1: ColumnModel, columnModel2: ColumnModel) => columnModel1['orderNbr'] - columnModel2['orderNbr'])

                            deferred.resolve(result)
                        }
                    })
                }
            })
        }
        return deferred.promise()
    }

}